import { useEffect, useState, useCallback } from 'react';
import {
  initPushNotifications,
  handlePushRegistrationFinished,
  isPushNotificationsEnabled,
  DEBOUNCE_REGISTER_DEVICE_MS,
} from 'shared/initPushNotifications';
import { getPushTokenFromLocalStorage } from 'state/localStorage';
import { useHistory } from 'react-router-dom';
import { useCacheState } from 'state/useCacheState';
import { useAuthState } from 'state/useAuthState';
import { Token } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { UseQueryResult } from '@tanstack/react-query';
import { QueryApiResult } from 'shared/types';
import {
  NotificationSettingAPI,
  NotificationSetting,
} from 'features/notifications/notifications.types';
import { useForegroundState } from 'hooks/useForegroundState';
import {
  CriticalAlerts,
  PermissionStatus,
} from 'libs/cap-plugins/critical-alerts';
import { useClearMapSelectionsForRouterEntity } from 'features/mapping/hooks/useClearMapSelectionsForRouterEntity';
import { useNotifications } from '../useNotifications';

type NotificationsSettingsProps = {
  regionSettingsQueryEnabled?: boolean;
};

type UseArePushNotificationsEnabledHookReturn = {
  isPushEnabled: boolean;
};

export type CriticalSettingsState = {
  criticalAlertsEnabled: boolean;
  criticalAlertsStatus: PermissionStatus;
  timeSensitiveEnabled: boolean;
  timeSensitiveStatus: PermissionStatus;
};

type UseArePushNotificationSettingsHookReturn = {
  isPushEnabled: boolean;
  pushToken: string | null;
  subscribedNotificationSettings: UseQueryResult<
    NotificationSettingAPI,
    unknown
  >;
  allNotificationSettings: QueryApiResult<NotificationSetting[]>;
  isIOS: boolean;
  criticalSettings: CriticalSettingsState;
};

export const useArePushNotificationsEnabled =
  (): UseArePushNotificationsEnabledHookReturn => {
    const [isPushEnabled, setIsPushEnabled] = useState(false);

    useEffect(() => {
      const checkPushPermissions = async (): Promise<void> => {
        const isEnabled = await isPushNotificationsEnabled();
        setIsPushEnabled(isEnabled);
      };

      checkPushPermissions();
    }, []);
    return {
      isPushEnabled,
    };
  };

/**
 * This custom hook component serves to manage notifications within the application.
 * It first checks whether notifications are enabled and whether a push token has
 * already been registered. If a push token has not been registered, the component
 * registers one when the user navigates to the notification settings to enable
 * notifications, and comes back to the WD app. Additionally, the component provides
 * notification settings data related to specific county regions.
 */
export const useNotificationsSettings = (
  props: NotificationsSettingsProps = {},
): UseArePushNotificationSettingsHookReturn => {
  const { regionSettingsQueryEnabled = true } = props;
  const history = useHistory();
  const { setCacheState } = useCacheState();
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [pushToken, setPushToken] = useState(getPushTokenFromLocalStorage());
  const { subscribedNotificationSettings, allNotificationSettings } =
    useNotifications({
      queryEnabled: regionSettingsQueryEnabled,
      pushToken: pushToken || undefined,
    });
  const appState = useForegroundState();
  const clearMapSelectionsForRouterEntity =
    useClearMapSelectionsForRouterEntity();
  const { user } = useAuthState();
  const isIOS = Capacitor.getPlatform() === 'ios';
  const [criticalSettings, setCriticalSettings] = useState<{
    critical: PermissionStatus;
    timeSensitive: PermissionStatus;
  }>({ critical: 'unknown', timeSensitive: 'unknown' });

  /**
   * When a user denies permission to receive push notifications on app start
   * and then grant permission when on this page, we have to wait for the 'registration'
   * event handler to execute so that we retry to get subscriptions with a token
   * if not token will be null and user is going to get stuck in an 'error' state
   */
  const handlePushTokenRegistration = useCallback(
    async (token: Token) => {
      await handlePushRegistrationFinished(token, user);
      // Mitigates a race condition where `useNotifications` queries may execute
      // before the device has fully registered with the API, resulting in missing
      // subscribed counties based on the user's location.
      //
      // To address this, a timeout longer than `DEBOUNCE_REGISTER_DEVICE_MS` is
      // introduced to allow sufficient time for device registration before
      // the queries run.
      setTimeout(
        () => setPushToken(token.value),
        DEBOUNCE_REGISTER_DEVICE_MS * 3,
      );
    },
    [user],
  );

  useEffect(() => {
    if (!appState.isActive) return;

    const checkPushPermissions = async (): Promise<void> => {
      const isEnabled = await isPushNotificationsEnabled();
      setIsPushEnabled(isEnabled);
      if (isEnabled && !getPushTokenFromLocalStorage()) {
        await initPushNotifications(
          history,
          setCacheState,
          handlePushTokenRegistration,
          clearMapSelectionsForRouterEntity,
        );
      }
    };
    checkPushPermissions();
  }, [
    appState.isActive,
    clearMapSelectionsForRouterEntity,
    handlePushTokenRegistration,
    history,
    setCacheState,
  ]);

  useEffect(() => {
    if (!appState.isActive || !isIOS) return;

    const checkAlertPermissions = async (): Promise<void> => {
      const result = await CriticalAlerts.checkPermission();
      setCriticalSettings({
        critical: result.critical,
        timeSensitive: result.timeSensitive,
      });
    };

    checkAlertPermissions();
  }, [appState.isActive, isIOS]);

  return {
    isPushEnabled,
    pushToken,
    subscribedNotificationSettings,
    allNotificationSettings,
    isIOS,
    criticalSettings: {
      criticalAlertsEnabled: criticalSettings.critical === 'granted',
      criticalAlertsStatus: criticalSettings.critical,
      timeSensitiveEnabled: criticalSettings.timeSensitive === 'granted',
      timeSensitiveStatus: criticalSettings.timeSensitive,
    },
  };
};
